import React from "react";
import clsx from "clsx";
import { IonButton, IonItemGroup, useIonViewWillEnter } from "@ionic/react";
import { useHistory } from "react-router-dom";

import { convertDateToDateString } from "../../../libs/Util";
import Dropdown from "../../../atoms/Dropdown";
import BoxAnalysisItem from "../../../atoms/BoxAnalysisItem";
import DatePicker from "../../../atoms/DatePicker";
import SearchField from "../../../atoms/SearchField";
import BoxDetailModal from "../../../molecules/BoxDetailModal";
import { ReactComponent as ArrowUpIcon } from "../../../assets/icons/arrow_up.svg";
import { ReactComponent as ArrowDownIcon } from "../../../assets/icons/arrow_down.svg";
import {
  BOX_SORT_DROPDOWN_DATA,
  EOrderType,
  EOrderBy,
} from "../../../constants/boxDashboard";
import {
  IClass,
  ITeacher,
  IBoxDashboard,
  IRealization,
  IStudentForGearbox,
  IPagyInfo,
  IOnClickStudentAvatarParams,
  IMyBox,
} from "../../../state";
import { manageBoxesPath } from "../../../store/teacher";

import styles from "./BoxDashboard.module.scss";

export interface IBoxesDashboardParams {
  title?: string;
  teacher_id?: number;
  class_id?: number;
  start_date?: Date | null;
  end_date?: Date | null;
  order_type?: EOrderType;
  order_by?: EOrderBy;
}

export interface BoxDashboardProps {
  isDisplaying: boolean;
  calling: boolean;
  filtering: boolean;
  showDetail: boolean;
  classes: IClass[];
  teachers: ITeacher[];
  boxList: IBoxDashboard[];
  realizations_count_of_left_students: number;
  myBox: IMyBox | null;
  gearboxStudents: IStudentForGearbox[];
  realizations: IRealization[];
  pagy_info: IPagyInfo;
  update: (args: { [key: string]: any }) => void;
  fetchBoxDashboard: (params: IBoxesDashboardParams) => void;
  fetchStudents: (box_id: number) => void;
  fetchRealizations: (box_id: number, page?: number) => void;
  fetchMyBoxRealizations: (page?: number) => void;
  toRealizationDetail: (realization_id: number) => void;
  showStudentDetail: (params: IOnClickStudentAvatarParams) => void;
  bulkCheckRealizations: (gear_box_id: number) => void;
}

const INITIAL_SEARCH = {
  searchText: "",
  selectedClassId: 0,
  selectedTeacherId: 0,
  startDate: null,
  endDate: null,
};

const BoxDashboard: React.FC<BoxDashboardProps> = ({
  isDisplaying,
  calling,
  filtering,
  showDetail,
  classes,
  teachers,
  boxList,
  realizations_count_of_left_students,
  myBox,
  gearboxStudents,
  realizations,
  pagy_info,
  update,
  fetchBoxDashboard,
  fetchStudents,
  fetchRealizations,
  fetchMyBoxRealizations,
  toRealizationDetail,
  showStudentDetail,
  bulkCheckRealizations,
}) => {
  const history = useHistory();

  const [state, setState] = React.useState<{
    searchText: string;
    selectedClassId: number;
    selectedTeacherId: number;
    startDate: Date | null;
    endDate: Date | null;
    orderType: EOrderType;
    orderBy: EOrderBy;
    boxId: number | null;
    isMyBox: boolean;
  }>({
    ...INITIAL_SEARCH,
    boxId: null,
    orderType: EOrderType.DESC,
    orderBy: EOrderBy.LATEST_REALIZATION_CREATED_AT,
    isMyBox: false,
  });

  const classesData = React.useMemo(
    () =>
      classes.map(cls => ({
        value: cls.id,
        label: `${cls.full_name} (${cls.year})`,
      })),
    [classes],
  );

  const teachersData = React.useMemo(
    () =>
      teachers.map(teacher => ({
        value: teacher.id,
        label: teacher.full_name ?? "",
      })),
    [teachers],
  );

  const largestRealizationsCount = React.useMemo(
    () =>
      (myBox ? [myBox, ...boxList] : boxList).reduce(
        (maxValue, { roots_count, will_count, exp_count }) => {
          const maxRealizationsCountOfThisBox = Math.max(
            roots_count,
            will_count,
            exp_count,
          );
          return maxValue > maxRealizationsCountOfThisBox
            ? maxValue
            : maxRealizationsCountOfThisBox;
        },
        0,
      ),
    [boxList, myBox],
  );

  const toggleSortType = React.useCallback(
    () =>
      setState(s => ({
        ...s,
        orderType:
          state.orderType === EOrderType.DESC
            ? EOrderType.ASC
            : EOrderType.DESC,
      })),
    [state.orderType],
  );

  const onInputChange = React.useCallback(
    e => setState(s => ({ ...s, searchText: e.target.value })),
    [],
  );

  const onDropdownSelect = React.useCallback(
    ({ name, value }) =>
      setState(s => ({
        ...s,
        [name]: value,
      })),
    [],
  );

  const onDatePickerChange = React.useCallback(([start, end]) => {
    setState(s => ({
      ...s,
      startDate: start,
      endDate: end,
    }));
  }, []);

  const onClearFilter = React.useCallback(
    () =>
      setState(s => ({
        ...s,
        ...INITIAL_SEARCH,
      })),
    [],
  );

  const isDatePickerInvalid = React.useMemo(
    () =>
      (!!state.startDate && !state.endDate) ||
      (!state.startDate && !!state.endDate),
    [state.startDate, state.endDate],
  );

  React.useEffect(() => {
    !filtering && onClearFilter();
  }, [filtering, onClearFilter]);

  React.useEffect(() => {
    !showDetail && setState(prevState => ({ ...prevState, boxId: null }));
  }, [showDetail]);

  const getBoxDashboard = React.useCallback(() => {
    fetchBoxDashboard({
      title: state.searchText,
      teacher_id: state.selectedTeacherId,
      class_id: state.selectedClassId,
      start_date: state.startDate,
      end_date: state.endDate,
      order_type: state.orderType,
      order_by: state.orderBy,
    });
  }, [
    fetchBoxDashboard,
    state.searchText,
    state.selectedTeacherId,
    state.selectedClassId,
    state.startDate,
    state.endDate,
    state.orderType,
    state.orderBy,
  ]);

  useIonViewWillEnter(() => {
    getBoxDashboard();
  });

  React.useEffect(() => {
    if (!isDatePickerInvalid || (!state.startDate && !state.endDate))
      getBoxDashboard();
  }, [state.startDate, state.endDate, isDatePickerInvalid, getBoxDashboard]);

  React.useEffect(() => {
    if (typeof state.boxId === "number") {
      if (!state.isMyBox) {
        const id = state.boxId;
        fetchStudents(id);
        fetchRealizations(id);
      } else {
        fetchMyBoxRealizations();
      }
      update({ showDetail: true });
    } else {
      update({ showDetail: false });
    }
  }, [
    fetchRealizations,
    fetchStudents,
    update,
    state.boxId,
    state.isMyBox,
    fetchMyBoxRealizations,
  ]);

  return (
    <div className={styles.wrapper}>
      {myBox && (
        <BoxAnalysisItem
          className={styles.myBox}
          isMyBox
          boxData={myBox}
          largestRealizationsCount={largestRealizationsCount}
          onClick={() => {
            setState(prevState => ({
              ...prevState,
              isMyBox: true,
              boxId: 0,
            }));
            bulkCheckRealizations(0);
          }}
        />
      )}
      <div className={styles.searchBox}>
        <div className={styles.label}>BOXを検索する</div>
        <div className={styles.searchOptions}>
          <SearchField
            className={clsx(styles.option)}
            searchText={state.searchText}
            onChange={onInputChange}
          />
          <Dropdown
            name="selectedClassId"
            className={clsx(styles.option)}
            data={classesData}
            defaultLabel="クラス/グループから探す"
            defaultValue={0}
            selectedValue={state.selectedClassId}
            onSelect={onDropdownSelect}
          />
          <Dropdown
            name="selectedTeacherId"
            className={clsx(
              styles.option,
              state.selectedTeacherId && styles.active,
            )}
            data={teachersData}
            defaultLabel="先生から探す"
            defaultValue={0}
            selectedValue={state.selectedTeacherId}
            onSelect={onDropdownSelect}
          />
          <DatePicker
            className={clsx(styles.option)}
            error={isDatePickerInvalid ? "開始日と終了日が必要です" : ""}
            label={
              state.startDate || state.endDate
                ? `${
                    state.startDate
                      ? convertDateToDateString(state.startDate)
                      : ""
                  }→${
                    state.endDate ? convertDateToDateString(state.endDate) : ""
                  }`
                : "BOXへの提出日を指定"
            }
            dateFormat="yyyy/MM/dd"
            locale="ja"
            selected={state.startDate}
            onChange={onDatePickerChange}
            startDate={state.startDate}
            endDate={state.endDate}
            selectsRange
          />
          <div className={styles.clearButtonWrapper}>
            <IonButton
              className={styles.clearButton}
              size="small"
              expand="full"
              disabled={calling}
              onClick={onClearFilter}
            >
              クリア
            </IonButton>
          </div>
        </div>
      </div>
      <div className={styles.sort}>
        <IonButton
          className={styles.btnAddBox}
          size="small"
          expand="full"
          onClick={() => history.push(manageBoxesPath)}
        >
          BOXをつくる
        </IonButton>
        <div className={styles.sortWrapper}>
          <button
            className={styles[state.orderType]}
            disabled={calling}
            onClick={toggleSortType}
          >
            <ArrowDownIcon />
            <ArrowUpIcon />
          </button>
          <span>並び順</span>
          <Dropdown
            name="orderBy"
            className={styles.sortDropdown}
            data={BOX_SORT_DROPDOWN_DATA.slice(1)}
            defaultLabel={BOX_SORT_DROPDOWN_DATA[0].label}
            defaultValue={EOrderBy.LATEST_REALIZATION_CREATED_AT}
            selectedValue={state.orderBy}
            onSelect={onDropdownSelect}
          />
        </div>
      </div>
      {boxList.length ? (
        <IonItemGroup className={styles.dataList}>
          {boxList.map(box => (
            <BoxAnalysisItem
              key={box.id}
              boxData={box}
              largestRealizationsCount={largestRealizationsCount}
              onClick={() => {
                setState(prevState => ({
                  ...prevState,
                  isMyBox: false,
                  boxId: box.id,
                }));
                bulkCheckRealizations(box.id);
              }}
            />
          ))}
        </IonItemGroup>
      ) : (
        <div className={styles.emptyData}>該当するデータがありません</div>
      )}
      <BoxDetailModal
        isMyBox={state.isMyBox}
        isDisplaying={isDisplaying}
        calling={calling}
        box={
          state.boxId === 0 ? myBox : boxList.find(b => b.id === state.boxId)!
        }
        largestRealizationsCount={largestRealizationsCount}
        realizations_count_of_left_students={
          realizations_count_of_left_students
        }
        gearboxStudents={gearboxStudents}
        realizations={realizations}
        pagy_info={pagy_info}
        fetchRealizations={fetchRealizations}
        fetchMyBoxRealizations={fetchMyBoxRealizations}
        onClose={() => setState(prevState => ({ ...prevState, boxId: null }))}
        toRealizationDetail={toRealizationDetail}
        showStudentDetail={showStudentDetail}
      />
    </div>
  );
};

export default BoxDashboard;
